/* eslint-disable hydrogen/prefer-image-component */
import {ReactElement, useMemo} from 'react';
import {Link} from '@shopify/hydrogen';

import {
  theWhiskyListLogoSmallWEBP,
  theWhiskyListLogoSmallPNG,
} from '../../assets/img';
import {SocialMediaHref} from '../../constants';
import {FooterNavColumnType} from '../../types';

import NewsletterSignup from '../NewsletterSignup.client';
import {useAuthService} from '../../contexts/AuthServiceProvider.client';
import VectorIcon from '../VectorIcon';

interface FooterProps {
  menuColumns: ReadonlyArray<FooterNavColumnType>;
}
export default function Footer({menuColumns}: FooterProps): ReactElement {
  const currentYear = useMemo(() => new Date().getFullYear(), []);

  const {authUser} = useAuthService();

  return (
    <footer className="main-footer">
      {/* Footer */}
      <div className="main-footer__container advantages-outer container">
        <div className="advantages">
          <div className="advantages__item">
            <div className="advantages__icon">
              <VectorIcon iconName="shipped" className="sprite-icon" />
            </div>
            <div className="advantages__content">
              <h6 className="advantages__title">Free Shipping</h6>
              <div className="advantages__text">On all orders over $200</div>
            </div>
          </div>
          <div className="advantages__item">
            <div className="advantages__icon">
              <VectorIcon iconName="insurance" className="sprite-icon" />
            </div>
            <div className="advantages__content">
              <h6 className="advantages__title">Every Order Insured</h6>
              <div className="advantages__text">For peace of mind</div>
            </div>
          </div>
          <div className="advantages__item">
            <div className="advantages__icon">
              <VectorIcon iconName="credit-card" className="sprite-icon" />
            </div>
            <div className="advantages__content">
              <h6 className="advantages__title">Payment Options</h6>
              <div className="advantages__text">
                All major cards, PayPal &amp; more
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="main-footer__container main-footer__border main-footer__seorow">
        <div className="main-footer__content container">
          <NewsletterSignup />
          <div className="main-footer__seorow-logo">
            <Link to="/">
              <picture>
                <source srcSet={theWhiskyListLogoSmallWEBP} type="image/webp" />
                <source srcSet={theWhiskyListLogoSmallPNG} type="image/png" />
                <img src={theWhiskyListLogoSmallPNG} alt="TWL logo" />
              </picture>
            </Link>
          </div>
          <div className="main-footer__seorow-menu">
            {menuColumns.map((column) => (
              <FooterNavigationColumn
                key={column.column_name}
                column={column}
              />
            ))}
          </div>
        </div>
      </div>
      <div className="main-footer__container main-footer__border main-footer__socialrow">
        <div className="main-footer__content container">
          <div className="main-footer__socialrow__payments">
            <span className="payment-icon amex-logo" title="Amex" />
            <span className="payment-icon apple-pay" title="Apple Pay" />
            <span className="payment-icon google-pay-logo" title="Google Pay" />
            <span className="payment-icon mastercard-logo" title="Mastercard" />
            <span className="payment-icon o-pay-logo" title="OPay" />
            <span className="payment-icon pay-pal-logo" title="PayPal" />
            <span className="payment-icon visa-logo" title="Visa" />
          </div>
          <div className="main-footer__socialrow__accounts">
            <ul className="main-footer__socialrow__accounts-menu">
              <li className="main-footer__socialrow__accounts-menu__item">
                <span className="main-footer__socialrow__accounts-menu__link">
                  <strong>Account</strong>
                </span>
              </li>
              {authUser ? (
                <li className="main-footer__socialrow__accounts-menu__item">
                  <Link
                    to="/my/profile"
                    className="main-footer__socialrow__accounts-menu__link"
                  >
                    Dashboard
                  </Link>
                </li>
              ) : (
                <>
                  <li className="main-footer__socialrow__accounts-menu__item">
                    <Link
                      to="/login/"
                      className="main-footer__socialrow__accounts-menu__link"
                    >
                      Login
                    </Link>
                  </li>
                  <li className="main-footer__socialrow__accounts-menu__item">
                    <Link
                      to="/signup/"
                      className="main-footer__socialrow__accounts-menu__link"
                    >
                      Join
                    </Link>
                  </li>
                </>
              )}
            </ul>
            <div className="main-footer__socialrow__accounts-smedia">
              <a
                href={SocialMediaHref.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="main-footer__socialrow__accounts-smedia-icon"
              >
                <span className="icon-instagram" />
              </a>
              <a
                href={SocialMediaHref.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="main-footer__socialrow__accounts-smedia-icon"
              >
                <span className="icon-facebook" />
              </a>
              <a
                href={SocialMediaHref.twitter}
                target="_blank"
                rel="noopener noreferrer"
                className="main-footer__socialrow__accounts-smedia-icon"
              >
                <span className="icon-twitter" />
              </a>
              <a
                href={SocialMediaHref.email}
                target="_blank"
                rel="noopener noreferrer"
                className="main-footer__socialrow__accounts-smedia-icon"
              >
                <span className="icon-email" />
              </a>
            </div>
          </div>
        </div>
      </div>
      <div className="main-footer__container main-footer__border">
        <div className="main-footer__copyright container">
          <div className="main-footer__copyright-left">
            <p>
              The prices listed on this site are correct to the best of our
              knowledge. The Whisky List offers no guarantee on accuracy or
              availability. Copyrights and trademarks are the property of their
              respective owners. The Whisky List supports the Responsible
              Service of Alcohol. NSW Liquor Act 2007. It is against the law to
              sell or supply alcohol to, or to obtain alcohol on behalf of, a
              person under the age of 18 years. Liquor licence number 51408293.
              Find out more at{' '}
              <a href="http://www.drinkwise.org.au">
                http://www.drinkwise.org.au
              </a>
              .
            </p>
            <p>Copyright &copy; {currentYear} The Whisky List.</p>
          </div>
          <div className="main-footer__copyright-right">
            <Link to="/">
              <picture>
                <source srcSet={theWhiskyListLogoSmallWEBP} type="image/webp" />
                <source srcSet={theWhiskyListLogoSmallPNG} type="image/png" />
                <img src={theWhiskyListLogoSmallPNG} alt="TWL logo" />
              </picture>
            </Link>
          </div>
        </div>
      </div>
      {/* /Footer */}
    </footer>
  );
}

interface FooterNavigationColumnProps {
  column: FooterNavColumnType;
}

function FooterNavigationColumn({column}: FooterNavigationColumnProps) {
  return (
    <div className="main-footer__seorow-menu__col">
      <h4 className="main-footer__seorow-menu__title">{column.column_name}</h4>
      <ul className="main-footer__seorow-menu__list">
        {column.footer_navigation_links.map((column) => (
          <li
            key={`${column?.link}${column?.link_text}`}
            className="main-footer__seorow-menu__item"
          >
            <Link
              to={column?.link}
              className="main-footer__seorow-menu__link"
              target={column?.link_target}
            >
              {column?.link_text}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* eslint-enable hydrogen/prefer-image-component */
